import { push } from 'redux-first-history';
import { combineEpics, ofType } from 'redux-observable';
import { EMPTY, of } from 'rxjs';
import { concat, concatMap, filter, ignoreElements, mergeMap, share, tap } from 'rxjs/operators';
import { isEoe, paths } from '../../common/constants/pathVariables.js';
import { SelectedPurposeOfUseOrContact } from '../../common/enums.js';
import {
  CREATE_NEW_CONTACT,
  getDuplicateContactDialogParams,
  getDuplicateContactInformationDialogParams,
  getPossibleDuplicateContactInformationDialogParams,
} from '../../common/utils/contactUtils.js';
import { ContactCreationType } from '../../components/ContactOrPurposeOfUse/ContactsOrPurposeOfUseUtils.js';
import { removeOmaLaiteLaskuSupportCase } from '../../components/EoeSubscriptionDetails/eppSubscriptionUtils.js';
import { ContactRole, ContactStatus, ContactType } from '../../generated/api/models.js';
import {
  changeSubscriptionFeaturesPrivateMethod,
  changeSubscriptionMobilePbxPrivateMethod,
  changeSubscriptionResumePrivateMethod,
  changeSubscriptionSuspendPrivateMethod,
  changeSubscriptionTerminatePrivateMethod,
  changeSubscriptionUserInformationPrivateMethod,
  createContactPrivateMethod,
  redeemOrTerminateEppPrivateMethod,
  updateContactPrivateMethod,
} from '../../generated/api/uiApiMethods.js';
import { openDialog } from '../../public/site/dialogStore.js';
import { saveContactCreated } from '../../public/site/subscriptionActionStore.js';
import {
  TypeKeys,
  addSupportCase,
  changeSubscriptionPbxConfigurationFailed,
  changeSubscriptionPbxConfigurationFulfilled,
  changeSubscriptionUserInfoFailed,
  changeSubscriptionUserInfoFulfilled,
  dispatchAuthAction,
  resumeSubscriptionFailedAction,
  resumeSubscriptionFulfilledAction,
  suspendSubscriptionFailed,
  suspendSubscriptionFulfilled,
  terminateSubscriptionActionFailed,
  terminateSubscriptionActionFulfilled,
} from '../actions/index.js';
import {
  changeSubscriptionAddonsFailed,
  changeSubscriptionAddonsFulfilled,
  changeSubscriptionUserInfoWithDuplicateFailed,
  createContactForSubscriptionUserInfoFailed,
  createContactForSubscriptionUserInfoFulfilled,
  postEppRedeemTerminateFailed,
  postEppRedeemTerminateFulfilled,
} from '../actions/subscriptionActionsActions.js';
import { callUiApi, prepareUiApiRequest } from '../common/uiApiUtils.js';
import { getConflictingErrors } from './authenticatedUserEpic.js';
import { actionToActionState, getMdmIdHeaderFromUrlParams } from './epicUtils.js';
import type { ActionAndState, EpicDependencies } from './epicUtils.js';
import type { PurposeOfUseOrContact } from '../../common/types/subscription.js';
import type {
  Contact,
  ContactUserContact,
  PutContactResponse,
  SubscriptionChangeFeaturesRequest,
  SubscriptionChangeUserInformationRequest,
  SubscriptionPbxConfiguration,
  UserPurposeOfUseDetails,
} from '../../generated/api/models.js';
import type {
  ActionTypes,
  ChangeSubscriptionPbxConfigurationAction,
  ChangeSubscriptionUserInfoAction,
  ChangeSubscriptionUserInfoFulfilledAction,
  ErrorAction,
  SuspendSubscriptionAction,
  TerminateSubscriptionAction,
} from '../actions/index.js';
import type {
  ChangeSubscriptionAddonsAction,
  CreateContactForSubscriptionUserInfoFulfilledAction,
  PostEppRedeemTerminateAction,
  PostEppRedeemTerminateFulfilledAction,
} from '../actions/subscriptionActionsActions.js';
import type { ActionWithId, State } from '../common/store.js';
import type { Action } from 'redux';
import type { ActionsObservable, Epic, StateObservable } from 'redux-observable';
import type { Observable } from 'rxjs';
import type { AjaxResponse } from 'rxjs/ajax';

const getNewContactPayload = (purposeOfUseOrContact: PurposeOfUseOrContact): Contact | undefined => {
  if (
    purposeOfUseOrContact.selected === SelectedPurposeOfUseOrContact.CONTACT &&
    (purposeOfUseOrContact.contactId === CREATE_NEW_CONTACT().value ||
      purposeOfUseOrContact.contactId === ContactCreationType.CREATE_NEW_CONTACT)
  ) {
    const userContact: ContactUserContact = {
      email: purposeOfUseOrContact.email,
      firstName: purposeOfUseOrContact.firstName!,
      lastName: purposeOfUseOrContact.lastName!,
      phoneNumber: purposeOfUseOrContact.phoneNumber,
      roles: [ContactRole.ENDUSER_CONTACT],
    };
    if (purposeOfUseOrContact.costCenter || purposeOfUseOrContact.employeeNumber) {
      return {
        contactType: ContactType.PERSON,
        person: {
          ...userContact,
          email: userContact.email!,
          phoneNumber: userContact.phoneNumber!,
          costCenter: purposeOfUseOrContact.costCenter,
          employeeNumber: purposeOfUseOrContact.employeeNumber,
        },
      };
    } else {
      return {
        contactType: ContactType.LIMITED_INFORMATION_PERSON,
        userContact: userContact,
      };
    }
  }
  return undefined;
};

export const terminateSubscription: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  prepareUiApiRequest(action$.pipe(ofType(TypeKeys.TERMINATE_SUBSCRIPTION)), (action: TerminateSubscriptionAction) =>
    actionToActionState(action, state$, 'pendingSubscriptionActions')
  ).pipe(
    concatMap((actionAndState: ActionAndState) => {
      const { subscriptionId, effectiveDate, donateNumber, terminateType } =
        actionAndState.action as TerminateSubscriptionAction;
      const payload = {
        donateNumber,
        effectiveDate,
        terminateType,
      };
      const successAction = (response: AjaxResponse) => terminateSubscriptionActionFulfilled(response.response);

      return callUiApi({
        epicDependencies,
        failureAction: terminateSubscriptionActionFailed,
        method: changeSubscriptionTerminatePrivateMethod(subscriptionId),
        payload,
        state$,
        successAction,
        headers: getMdmIdHeaderFromUrlParams(),
      });
    })
  );

export const suspendSubscriptionActionEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  prepareUiApiRequest(action$.pipe(ofType(TypeKeys.SUSPEND_SUBSCRIPTION)), (action: ActionWithId) =>
    actionToActionState(action, state$, 'pendingSubscriptionActions')
  ).pipe(
    concatMap((actionAndState: ActionAndState) => {
      const subscriptionId = (actionAndState.action as SuspendSubscriptionAction).subscriptionId;
      const successAction = (response: AjaxResponse) => suspendSubscriptionFulfilled(response.response);

      return callUiApi({
        epicDependencies,
        failureAction: suspendSubscriptionFailed,
        method: changeSubscriptionSuspendPrivateMethod(subscriptionId),
        state$,
        successAction,
        headers: getMdmIdHeaderFromUrlParams(),
      });
    })
  );

export const resumeSubscriptionActionEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  prepareUiApiRequest(action$.pipe(ofType(TypeKeys.RESUME_SUBSCRIPTION)), (action: ActionWithId) =>
    actionToActionState(action, state$, 'pendingSubscriptionActions')
  ).pipe(
    concatMap((actionAndState: ActionAndState) => {
      const subscriptionId = (actionAndState.action as SuspendSubscriptionAction).subscriptionId;
      const successAction = (response: AjaxResponse) => resumeSubscriptionFulfilledAction(response.response);
      return callUiApi({
        epicDependencies,
        failureAction: resumeSubscriptionFailedAction,
        method: changeSubscriptionResumePrivateMethod(subscriptionId),
        state$,
        successAction,
        headers: getMdmIdHeaderFromUrlParams(),
      });
    }),
    share()
  );

function createContactForSubscriptionChange<T>(
  contact: Contact,
  epicDependencies: EpicDependencies,
  state$: StateObservable<State>,
  successAction: (response: PutContactResponse) => T
): Observable<T | ErrorAction<TypeKeys>> {
  const forceUpsert = contact.contactStatus === ContactStatus.PASSIVE;
  if (contact.contactId) {
    const { contactId, ...payload } = contact;
    return callUiApi({
      epicDependencies,
      failureAction: createContactForSubscriptionUserInfoFailed,
      method: updateContactPrivateMethod(contactId, { forceUpsert, bothNamesChanged: false }),
      payload,
      state$,
      successAction: (response: AjaxResponse) =>
        successAction({
          ...contact,
          lastModified: response.response.lastModified,
        }),
      headers: getMdmIdHeaderFromUrlParams(),
    });
  }
  return callUiApi({
    epicDependencies,
    failureAction: createContactForSubscriptionUserInfoFailed,
    method: createContactPrivateMethod({ forceUpsert }),
    payload: contact,
    state$,
    successAction: (response: AjaxResponse) =>
      successAction({
        ...contact,
        contactId: response.response.contactId,
        lastModified: response.response.lastModified,
      }),
    headers: getMdmIdHeaderFromUrlParams(),
  });
}

export const changeSubscriptionUserInfoEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  prepareUiApiRequest(
    action$.pipe(ofType(TypeKeys.CHANGE_SUBSCRIPTION_USER_INFO)),
    (action: ChangeSubscriptionUserInfoAction) => actionToActionState(action, state$, 'pendingSubscriptionActions')
  ).pipe(
    concatMap((actionAndState: ActionAndState) => {
      if (!actionAndState.state) {
        throw new Error('invalid action state for change subscription user info');
      }
      const { purposeOfUseOrContact, subscriptionId, subscriptionDisplayId, billingAccountIdForOmaLaiteLaskuRemoval } =
        actionAndState.action as ChangeSubscriptionUserInfoAction;
      let observable: Observable<
        CreateContactForSubscriptionUserInfoFulfilledAction | ErrorAction<TypeKeys> | ChangeSubscriptionUserInfoAction
      > = of(actionAndState.action as ChangeSubscriptionUserInfoAction);
      const newContact = getNewContactPayload(purposeOfUseOrContact);

      if (newContact) {
        observable = createContactForSubscriptionChange(
          newContact,
          epicDependencies,
          state$,
          (response: PutContactResponse) => {
            return createContactForSubscriptionUserInfoFulfilled(response, newContact);
          }
        );
      }
      return observable.pipe(
        concatMap(action => {
          let userPurposeOfUseDetails: UserPurposeOfUseDetails;
          if (action?.type === TypeKeys.CREATE_CONTACT_FOR_SUBSCRIPTION_USER_INFO_FULFILLED) {
            userPurposeOfUseDetails = {
              contactId: (action as CreateContactForSubscriptionUserInfoFulfilledAction).contact.contactId,
            };
          } else if (purposeOfUseOrContact.selected === SelectedPurposeOfUseOrContact.PURPOSEOFUSE) {
            userPurposeOfUseDetails = {
              purposeOfUse: purposeOfUseOrContact.purposeOfUse,
            };
          } else if (
            action?.type === TypeKeys.CREATE_CONTACT_FOR_SUBSCRIPTION_USER_INFO_FAILED &&
            action.errors?.[0].source?.error?.duplicateContact?.contactId
          ) {
            const dialogParams =
              action.errors?.[0].source?.error?.errorType === 'POSSIBLE_CONTACT_DUPLICATE'
                ? getPossibleDuplicateContactInformationDialogParams(action.errors[0].source.error.duplicateContact)
                : getDuplicateContactInformationDialogParams(action.errors[0].source.error.duplicateContact);
            openDialog(dialogParams);
            return [changeSubscriptionUserInfoWithDuplicateFailed('Failed to create contact', 500)];
          } else if (
            action?.type === TypeKeys.CREATE_CONTACT_FOR_SUBSCRIPTION_USER_INFO_FAILED &&
            getConflictingErrors(action.errors)
          ) {
            openDialog(getDuplicateContactDialogParams());
            return [changeSubscriptionUserInfoWithDuplicateFailed('Failed to create contact', 500)];
          } else {
            userPurposeOfUseDetails = {
              contactId: purposeOfUseOrContact.contactId,
            };
          }

          const payload: SubscriptionChangeUserInformationRequest = {
            contactDetails: userPurposeOfUseDetails,
            costCenter: purposeOfUseOrContact.costCenter,
            voiceDirectoryDetails: purposeOfUseOrContact.directoryListingMobileNumberPublicity?.voiceDirectoryDetails,
            pbxDirectoryDetails: purposeOfUseOrContact.directoryListingCorporateNumberPublicity?.pbxDirectoryDetails,
            subscriptionReference: purposeOfUseOrContact.employeeNumber,
          };

          const successAction = ({ response }: AjaxResponse) => {
            return changeSubscriptionUserInfoFulfilled(
              payload,
              response,
              subscriptionId,
              billingAccountIdForOmaLaiteLaskuRemoval,
              subscriptionDisplayId
            );
          };

          // if both voice & pbx are changed, sfdc needs separate calls :/
          if (
            purposeOfUseOrContact.directoryListingMobileNumberPublicity?.voiceDirectoryDetails &&
            purposeOfUseOrContact.directoryListingCorporateNumberPublicity?.pbxDirectoryDetails
          ) {
            const firstChangeUserInformation$ = callUiApi({
              epicDependencies,
              failureAction: changeSubscriptionUserInfoFailed,
              method: changeSubscriptionUserInformationPrivateMethod(subscriptionId),
              payload: {
                ...payload,
                pbxDirectoryDetails: undefined,
              },
              state$,
              successAction,
            });

            const secondChangeUserInformation$ = callUiApi({
              epicDependencies,
              failureAction: changeSubscriptionUserInfoFailed,
              method: changeSubscriptionUserInformationPrivateMethod(subscriptionId),
              payload: {
                ...payload,
                voiceDirectoryDetails: undefined,
              },
              state$,
              successAction,
            });

            return firstChangeUserInformation$.pipe(
              concatMap(() => {
                return secondChangeUserInformation$.pipe(
                  concat(
                    of(action).pipe(
                      filter(() => {
                        return action?.type === TypeKeys.CREATE_CONTACT_FOR_SUBSCRIPTION_USER_INFO_FULFILLED;
                      })
                    )
                  )
                );
              })
            );
          } else {
            const changeUserInformation$ = callUiApi({
              epicDependencies,
              failureAction: changeSubscriptionUserInfoFailed,
              method: changeSubscriptionUserInformationPrivateMethod(subscriptionId),
              payload,
              state$,
              successAction,
              headers: getMdmIdHeaderFromUrlParams(),
            });
            return changeUserInformation$.pipe(
              concat(
                of(action).pipe(
                  filter(() => {
                    return action?.type === TypeKeys.CREATE_CONTACT_FOR_SUBSCRIPTION_USER_INFO_FULFILLED;
                  })
                )
              )
            );
          }
        })
      );
    })
  );

export const changeSubscriptionUserInfoFulfilledEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>
) =>
  action$.pipe(
    ofType(TypeKeys.CHANGE_SUBSCRIPTION_USER_INFO_FULFILLED),
    mergeMap((action: ChangeSubscriptionUserInfoFulfilledAction) => {
      if (action.billingAccountIdForOmaLaiteLaskuRemoval) {
        return of(
          ...([
            addSupportCase(
              removeOmaLaiteLaskuSupportCase(
                action.subscriptionId,
                action.subscriptionDisplayId || '',
                action.billingAccountIdForOmaLaiteLaskuRemoval
              ),
              true
            ),
          ].filter(Boolean) as Action[])
        );
      }
      return EMPTY;
    })
  );

export const changeSubscriptionPbxConfigurationEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  prepareUiApiRequest(action$.pipe(ofType(TypeKeys.CHANGE_SUBSCRIPTION_PBX_CONFIGURATION)), (action: ActionWithId) =>
    actionToActionState(action, state$, 'pendingSubscriptionActions')
  ).pipe(
    concatMap((actionAndState: ActionAndState) => {
      const { subscriptionId, pbxConfiguration } = actionAndState.action as ChangeSubscriptionPbxConfigurationAction;
      const payload = {
        ...pbxConfiguration,
        pbxConfigurationDetails: {
          ...pbxConfiguration.pbxConfigurationDetails,
          offWorkReachabilityChain: pbxConfiguration.pbxConfigurationDetails.offWorkReachabilityChain
            ? pbxConfiguration.pbxConfigurationDetails.offWorkReachabilityChain.filter(
                phoneNumber => phoneNumber.length > 0
              )
            : undefined,
        },
        workingHoursReachabilityChain: pbxConfiguration.workingHoursReachabilityChain
          ? pbxConfiguration.workingHoursReachabilityChain.filter(phoneNumber => phoneNumber.length > 0)
          : undefined,
      } as SubscriptionPbxConfiguration;

      return callUiApi({
        epicDependencies,
        failureAction: changeSubscriptionPbxConfigurationFailed,
        method: changeSubscriptionMobilePbxPrivateMethod(subscriptionId),
        payload,
        state$,
        successAction: (response: AjaxResponse) =>
          changeSubscriptionPbxConfigurationFulfilled(response.response, subscriptionId),
        headers: getMdmIdHeaderFromUrlParams(),
      });
    })
  );

export const changeSubscriptionAddonsEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) => {
  return prepareUiApiRequest(action$.pipe(ofType(TypeKeys.CHANGE_ADDONS)), (action: ActionWithId) =>
    actionToActionState(action, state$, 'pendingSubscriptionActions')
  ).pipe(
    concatMap((actionAndState: ActionAndState) => {
      const { subscription, addOnsToAdd, addOnsToRemove, addOnsToUpdate, addOnAttributes } =
        actionAndState.action as ChangeSubscriptionAddonsAction;
      const payload: SubscriptionChangeFeaturesRequest = {
        addOnChanges: {
          addOnsToAdd: addOnsToAdd.map(addOnGuid => ({
            addOnCode: addOnGuid,
            addOnAttributes: addOnAttributes && addOnAttributes[addOnGuid],
          })),
          addOnsToRemove: addOnsToRemove.map(addOnGuid => ({ addOnCode: addOnGuid })),
          addOnsToUpdate: addOnsToUpdate?.map(addOnGuid => ({
            addOnCode: addOnGuid,
            addOnAttributes: addOnAttributes && addOnAttributes[addOnGuid],
          })),
        },
        msisdn: subscription.subscriptionUserFriendlyId, // Used with assets
        sourceSystem: subscription.sourceSystem,
      };
      const successAction = (response: AjaxResponse) =>
        changeSubscriptionAddonsFulfilled(response.response, addOnsToAdd, addOnsToRemove);
      return callUiApi({
        epicDependencies,
        failureAction: changeSubscriptionAddonsFailed,
        method: changeSubscriptionFeaturesPrivateMethod(subscription.subscriptionId),
        payload,
        state$,
        successAction,
        headers: getMdmIdHeaderFromUrlParams(),
      });
    })
  );
};

export const postEppRedeemTerminate = (
  postEppRedeemTerminateAction: PostEppRedeemTerminateAction,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
): Observable<PostEppRedeemTerminateFulfilledAction | ErrorAction<TypeKeys>> => {
  return callUiApi({
    epicDependencies,
    state$,
    method: redeemOrTerminateEppPrivateMethod(postEppRedeemTerminateAction.subscriptionId),
    payload: postEppRedeemTerminateAction.request,
    failureAction: postEppRedeemTerminateFailed,
    successAction: (response: AjaxResponse) =>
      postEppRedeemTerminateFulfilled(
        postEppRedeemTerminateAction.request,
        response.response,
        postEppRedeemTerminateAction.subscriptionId
      ),
    headers: getMdmIdHeaderFromUrlParams(),
  });
};

export const postEppRedeemTerminateFulFilledEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>
) => {
  return action$.pipe(
    ofType(TypeKeys.POST_EPP_REDEEM_TERMINATE_FULFILLED),
    tap((action: PostEppRedeemTerminateFulfilledAction) => {
      dispatchAuthAction('FILTER_EXPIRING_EPP_SUBS', action.subscriptionId);
    }),
    mergeMap(() => {
      return [push(isEoe(window.location.pathname) ? paths.EMPLOYEE_DEVICES : paths.PS_DEVICES)];
    })
  );
};

export const syncContactCreatedToStoreEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>
) =>
  action$.pipe(
    ofType(TypeKeys.CREATE_CONTACT_FOR_SUBSCRIPTION_USER_INFO_FULFILLED),
    tap((action: CreateContactForSubscriptionUserInfoFulfilledAction) =>
      saveContactCreated(action.contact, action.billingAccountContactIdRef)
    ),
    ignoreElements()
  );

export const subscriptionActionEpic: Epic<ActionTypes, Action, State, EpicDependencies> = combineEpics(
  terminateSubscription,
  suspendSubscriptionActionEpic,
  resumeSubscriptionActionEpic,
  changeSubscriptionAddonsEpic,
  changeSubscriptionUserInfoEpic,
  changeSubscriptionUserInfoFulfilledEpic,
  changeSubscriptionPbxConfigurationEpic,
  syncContactCreatedToStoreEpic,
  postEppRedeemTerminateFulFilledEpic
);
